const videoSelector = 'video.fnk-autoplay-video';
const catalogVideoSelector = 'video.fnk-home-catalog__video';
const catalogTemplateSelector = 'template[data-fnk-catalog-video]';
// TikTok WebViews can force native fullscreen even when playsInline is set.
const isTikTokBrowser = /tiktok|musical_ly|bytedancewebview|\btrill[_/]/i.test(navigator.userAgent);
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

const isVisible = (video) => {
  const rect = video.getBoundingClientRect();
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom >= 0 &&
    rect.right >= 0 &&
    rect.top <= window.innerHeight &&
    rect.left <= window.innerWidth
  );
};

const attemptPlayback = (video) => {
  if (isTikTokBrowser && video.matches(catalogVideoSelector)) return;
  if (reducedMotionQuery.matches || document.hidden || !isVisible(video)) return;

  video.muted = true;
  video.playsInline = true;
  const playPromise = video.play();
  if (playPromise) playPromise.catch(() => {});
};

const visibilityObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      if (entry.isIntersecting) {
        attemptPlayback(video);
      } else {
        video.pause();
      }
    });
  },
  { rootMargin: '160px 0px', threshold: 0.01 }
);

const registerVideo = (video) => {
  if (video.dataset.fnkAutoplayManaged === 'true') return;

  video.dataset.fnkAutoplayManaged = 'true';
  video.addEventListener('playing', () => video.classList.add('fnk-autoplay-video--playing'));
  video.addEventListener('pause', () => video.classList.remove('fnk-autoplay-video--playing'));
  video.addEventListener('error', () => video.classList.remove('fnk-autoplay-video--playing'));
  visibilityObserver.observe(video);
  attemptPlayback(video);
};

const registerVideos = (root = document) => {
  // A template's contents cannot load or autoplay before this browser check.
  // Keep the image in place if JavaScript is unavailable or motion is disabled.
  if (!isTikTokBrowser && !reducedMotionQuery.matches) {
    const templates = [...(root.querySelectorAll?.(catalogTemplateSelector) ?? [])];
    if (root instanceof HTMLTemplateElement && root.matches(catalogTemplateSelector)) templates.unshift(root);
    templates.forEach((template) => {
      const video = template.content.querySelector(videoSelector)?.cloneNode(true);
      if (!video) return;
      template.replaceWith(video);
      registerVideo(video);
    });
  }
  if (root instanceof HTMLVideoElement && root.matches(videoSelector)) registerVideo(root);
  root.querySelectorAll?.(videoSelector).forEach(registerVideo);
};

const resumeVisibleVideos = () => {
  document.querySelectorAll(videoSelector).forEach(attemptPlayback);
};

const mutationObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => registerVideos(node)));
});

const initialize = () => {
  registerVideos();
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}

['touchstart', 'pointerdown', 'click', 'keydown', 'scroll'].forEach((eventName) => {
  document.addEventListener(eventName, resumeVisibleVideos, { capture: true, passive: true });
});

document.addEventListener('visibilitychange', resumeVisibleVideos);
document.addEventListener('shopify:section:load', (event) => registerVideos(event.target));
